/**
 * EO «Финансы» — факт+прогноз выручки за период (V1 FinAnalyticsView,
 * index.html:7566-8023 → V4). Период приходит из шапки EoScreen (DateSelector
 * range) + локальные пресет-чипы (Этот/Прош./След. месяц, Эта неделя — UTC+5),
 * которые дёргают onSetRange (EoScreen синхронизирует и стейт, и лейбл
 * триггера через window.DateSelector.setRange).
 *
 * KPI-плитки: Факт ↗ (модалка «Отведённые игры»), Прогноз ↗ («Предстоящие»),
 * Итого; Буд. брони / Отведено / Отменено. Ниже: график по дням, филиалы,
 * доли оплат, плашка полученной предоплаты. Read-only (perm finance:read).
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { useFinanceAnalytics } from "../../hooks/useFinanceAnalytics";
import { useFinanceGames } from "../../hooks/useFinanceGames";
import { BranchBars, MiniBars, PayShare } from "./EoFinanceCharts";
import { FutureBookingsModal, GamesListModal } from "./EoFinanceModals";
import { GamesDonePop } from "../../components/shared/GamesDonePop";
import { useGamesStats } from "../../hooks/gamesStats";
import { addDays, fmtDateApi, todayBusiness } from "../../utils/date";
import css from "./finance.module.css";

const rub = (n: number) => `₽${Math.round(n || 0).toLocaleString("ru-RU")}`;

/* Месяц (y, m может выходить за 0..11 — Date.UTC нормализует) → границы API. */
function monthApi(y: number, m: number): { from: string; to: string } {
  return {
    from: fmtDateApi(new Date(Date.UTC(y, m, 1))),
    to: fmtDateApi(new Date(Date.UTC(y, m + 1, 0))),
  };
}

interface KpiTile { label: string; num: string; cls?: string; sub: ReactNode; onTap?: (el: HTMLElement) => void }

function Tile({ t }: { t: KpiTile }) {
  const body = (
    <>
      <span className={css.kpiLabel}>{t.label}</span>
      <span className={`${css.kpiNum} ${t.cls ?? ""}`}>{t.num}</span>
      <span className={css.kpiSub}>{t.sub}</span>
    </>
  );
  return t.onTap ? (
    <button type="button" className={`${css.kpiCard} ${css.kpiTap}`} onClick={(e) => t.onTap?.(e.currentTarget)}>{body}</button>
  ) : (
    <div className={css.kpiCard}>{body}</div>
  );
}

export function EoFinanceView({ from, to, onSetRange }: {
  from: string; to: string; onSetRange: (from: string, to: string) => void;
}) {
  const fin = useFinanceAnalytics(from, to);
  const lists = useFinanceGames(from, to);
  const [showGames, setShowGames] = useState(false);
  const [showFuture, setShowFuture] = useState(false);
  /* 26.10 (владелец): тап по «Отведено» — дропдаун с подробной статистикой (тот же «отведено», что здесь) */
  const gs = useGamesStats(from, to);
  const [donePop, setDonePop] = useState<HTMLElement | null>(null);

  const presets = useMemo(() => {
    const t = todayBusiness();
    const y = t.getUTCFullYear();
    const m = t.getUTCMonth();
    const mon = addDays(t, -((t.getUTCDay() || 7) - 1)); // понедельник текущей недели
    return [
      { label: "Этот месяц", ...monthApi(y, m) },
      { label: "Прош. месяц", ...monthApi(y, m - 1) },
      { label: "След. месяц", ...monthApi(y, m + 1) },
      { label: "Эта неделя", from: fmtDateApi(mon), to: fmtDateApi(addDays(mon, 6)) },
    ];
  }, []);

  const d = fin.data;
  const totalRev = d?.completed.revenue ?? 0;
  const estRev = d?.estRevenue ?? 0;
  /* 28.08 (владелец): зарплатный фонд периода — той же логикой, что HR-«Зарплаты» */
  const [salaryFund, setSalaryFund] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    setSalaryFund(null);
    if (!from || !to) return;
    /* 13.10 архив 1С (ПР3): фонд — всё начисленное, и выплаченное через 1С (paid_1c_total вне total), иначе «Резерв» прошлых периодов завышен */
    api<{ results?: { total?: number; paid_1c_total?: number }[] }>(`/api/finance/salary?from=${from}&to=${to}`)
      .then((r) => { if (alive) setSalaryFund((r.results ?? []).reduce((a, x) => a + (Number(x.total) || 0) + (Number(x.paid_1c_total) || 0), 0)); })
      .catch(() => { if (alive) setSalaryFund(0); });
    return () => { alive = false; };
  }, [from, to]);
  const avgCheck = d && d.completed.gamesDone > 0 ? totalRev / d.completed.gamesDone : 0;
  const cancelPct = d && (d.allBookedCount + d.cancelled) > 0 ? Math.round((d.cancelled / (d.allBookedCount + d.cancelled)) * 100) : 0;
  const openGames = () => { setShowGames(true); lists.loadGames(); };
  const openFuture = () => { setShowFuture(true); lists.loadFuture(); };

  const row1: KpiTile[] = d ? [
    { label: "Факт. выручка ↗", num: rub(totalRev), cls: css.green, sub: `${d.conducted} игр отведено`, onTap: openGames },
    { label: "Прогноз (буд.) ↗", num: rub(estRev), cls: css.blue, sub: `${d.futureBooked.count} броней`, onTap: openFuture },
    { label: "Итого", num: rub(totalRev + estRev), cls: css.cyan, sub: "факт + прогноз" },
  ] : [];
  const row2: KpiTile[] = d ? [
    {
      label: "Буд. брони ↗", num: String(d.futureBooked.count),
      sub: <>без предоплаты: <span className={css.orange}>{d.futureBooked.noPrepaidCount}</span></>,
      onTap: openFuture,
    },
    { label: "Отведено ▾", num: String(d.conducted), cls: css.green, sub: gs.data ? `${gs.data.players} игроков в них` : `${d.allBookedPlayers} игроков по броням`,   /* 26.10: было — игроки ВСЕХ броней периода */
      onTap: (el) => setDonePop((cur) => (cur ? null : el)) },
    { label: "Отменено", num: `${d.cancelled} · ${cancelPct}%`, cls: d.cancelled > 0 ? css.red : css.muted, sub: "от всех броней периода" },
  ] : [];
  /* 28.08 (владелец): средний чек · зарплатный фонд · резерв (выручка − ЗП) */
  const row3: KpiTile[] = d ? [
    { label: "Средний чек", num: rub(avgCheck), cls: css.cyan, sub: `по ${d.completed.gamesDone} играм` },
    { label: "Зарплатный фонд", num: salaryFund == null ? "…" : rub(salaryFund), cls: css.orange, sub: "той же логикой, что HR" },
    { label: "Резерв", num: salaryFund == null ? "…" : rub(totalRev - salaryFund), cls: totalRev - (salaryFund ?? 0) >= 0 ? css.green : css.red, sub: "факт − зарплаты" },
  ] : [];

  const emptyPeriod = !!d && totalRev === 0 && estRev === 0
    && d.conducted === 0 && d.futureBooked.count === 0 && d.cancelled === 0;

  return (
    <div className={css.wrap}>
      <div className={css.presetRow}>
        {presets.map((p) => (
          <button
            key={p.label} type="button"
            className={`${css.pchip} ${from === p.from && to === p.to ? css.pchipOn : ""}`}
            onClick={() => onSetRange(p.from, p.to)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {fin.loading ? (
        <div className={css.skel}><div className={css.skRow} /><div className={css.skRow} /><div className={css.skRow} /></div>
      ) : fin.error ? (
        <div className={css.empty}>
          Не удалось загрузить аналитику: {fin.error}<br />
          <button type="button" className={css.retry} onClick={fin.reload}>Повторить</button>
        </div>
      ) : d ? (
        <>
          <div className={css.kpi3}>{row1.map((t) => <Tile key={t.label} t={t} />)}</div>
          <div className={css.kpi3}>{row2.map((t) => <Tile key={t.label} t={t} />)}</div>
          <div className={css.kpi3}>{row3.map((t) => <Tile key={t.label} t={t} />)}</div>

          {emptyPeriod ? (
            <div className={css.empty}>За период нет ни отчётов, ни броней</div>
          ) : (
            <>
              <div className={css.card}>
                <div className={css.cardTitle}>Выручка и брони по дням</div>
                <MiniBars daily={d.dailyRevenue} future={d.futureByDate} />
              </div>

              {d.byQuest.length > 0 && (
                <div className={css.card}>
                  <div className={css.cardTitle}>По квестам</div>
                  {d.byQuest.map((q) => (
                    <div key={q.questId} className={css.qRow}>
                      <span className={css.qName}>{q.questShort}</span>
                      <span className={css.qBranch}>{q.branchName}</span>
                      <span className={css.qGames}>{q.games} игр</span>
                      <span className={css.qRev}>{rub(q.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}

              {(d.byBranch.length > 0 || d.estByBranch.length > 0) && (
                <div className={css.card}>
                  <div className={css.cardTitle}>По филиалам</div>
                  <BranchBars byBranch={d.byBranch} estByBranch={d.estByBranch} />
                </div>
              )}

              {totalRev > 0 && (
                <div className={css.card}>
                  <div className={css.cardTitle}>Способы оплаты</div>
                  <PayShare completed={d.completed} />
                </div>
              )}

              {d.futureBooked.prepaidCollected > 0 && (
                <div className={css.note}>
                  Предоплата по буд. броням уже получена: <b>{rub(d.futureBooked.prepaidCollected)}</b>
                </div>
              )}
            </>
          )}
        </>
      ) : null}

      {showGames && (
        <GamesListModal from={from} to={to} state={lists.games} onClose={() => setShowGames(false)} onRetry={lists.loadGames} />
      )}
      <GamesDonePop open={!!donePop} anchor={donePop} stats={gs.data} error={gs.error} onClose={() => setDonePop(null)} />
      {showFuture && (
        <FutureBookingsModal from={from} to={to} state={lists.future} onClose={() => setShowFuture(false)} onRetry={lists.loadFuture} />
      )}
    </div>
  );
}
